import { mapToSelectOptions } from '../../../infrastructure/helpers/html/selectOptionHelper';
import { reportTypeDisplayMap } from '../../../infrastructure/constants/reportType';
import dataSourceKind from '../../../infrastructure/constants/dataSourceKind';
import IngestionConfigOption from '../../../types/ingestion/IngestionConfigOption';
import ReportConfig from '../../../types/report/ReportConfig';
import SelectOption from '../../../infrastructure/types/SelectOption';
import reportSubRegime from '../../../infrastructure/constants/reportSubRegime';
import ReportVariable from '../../../types/report/ReportVariable';
import operandHelper from '../../../infrastructure/helpers/functions/operand/operandHelper';
import Operand from '../../../types/functions/Operand/Operand';
import IngestionConfig from '../../../types/ingestion/IngestionConfig';
import OptionalModule from '../../../types/report/OptionalModule';
import { endpointFilterType } from '../../../infrastructure/constants/report/endpointFilterType';

const mapToselectOptions = (module: string): SelectOption => {
    return {
        text: module,
        value: module
    };
};

const getModules = (): SelectOption[] => {

    let options: SelectOption[] = [{ text: '', value: '' }];

    Object.values(OptionalModule).forEach((v) => {
        options.push(mapToselectOptions(v));
    });

    return options;
}

const getConfigOptionValue = (config: IngestionConfigOption): string => (config.id as number).toString();
const getConfigOptionText = (config: IngestionConfigOption): string => {

    if (config.dataSourceKind === dataSourceKind.accuracy ||
        config.dataSourceKind === dataSourceKind.globalRef) {

        return config.name;
    }

    return `${config.name} (${config.dataSourceKind})`
};

const isDataSourceReferencedInCurrentConfig = (dataSourceId: number, reportConfig: ReportConfig, dataSources?: IngestionConfig[] | null): boolean => {
    return (
        reportConfig.isinSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.leiSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.exchangeRatesSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.annaDsbUpiEnrichmentSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.lseSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.symbolSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.firdsFcaInstrumentSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.firdsEsmaInstrumentSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.annaDsbUpiSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.fcaRegulatedEntitiesSelectors?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.lookups?.some(x => x.dataSource1Id === dataSourceId || x.dataSource2Id === dataSourceId) ||
        reportConfig.variables?.some(x => x.dataSourceId === dataSourceId) ||
        getVariableDataSourceUsed(reportConfig.variables, dataSources).some(x => x === dataSourceId) ||
        reportConfig.filters?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.cases?.some(x => x.dataSourceId === dataSourceId) ||
        reportConfig.dictionaries?.some(x => x.dataSourceId === dataSourceId)
    );
}

const getVariableDataSourceUsed = (variables: ReportVariable[], dataSources?: IngestionConfig[] | null): number[] => {

    if (!dataSources || dataSources.length === 0) {
        return [];
    }

    let fieldsMap = new Map<number, number>();

    dataSources.forEach((ds) => {
        ds.fieldDefinitions.forEach((field) => {
            fieldsMap.set(field.id as number, ds.id as number);
        });
    });

    let uniqueDataSourceIds = new Set<number>();

    variables.forEach((v) => {

        operandHelper.proceedWhitAssignment(v.assignment, (operand: Operand) => {
            if (operand.fieldId && operand.fieldId > 0) {

                let id = fieldsMap.get(operand.fieldId as number) as number;
                uniqueDataSourceIds.add(id);
            }
        });
    });

    return Array.from(uniqueDataSourceIds);
}

const runAllOptions: SelectOption[] = [
    { text: 'Latest Only', value: "0" },
    { text: 'Run All', value: "1" },
    { text: 'Oldest Only', value: "2" }
];

const reportTypeOptions: SelectOption[] = Object.keys(reportTypeDisplayMap).map(key => { return { value: key, text: reportTypeDisplayMap[key] } });

const getSubRegimeOptions = (regime: string) => mapToSelectOptions(reportSubRegime(regime));

const regimeOptions = (regimes: { [key: string]: any }) => mapToSelectOptions(regimes);

const isRemoveOptionalModuleDisabled = (module: string, reportConfig: ReportConfig): boolean => {

    let isAnyCacheKeysDefined = reportConfig.annaDsbUpiEnrichmentSelectors.length > 0 ||
        reportConfig.annaDsbUpiSelectors.length > 0 ||
        reportConfig.exchangeRatesSelectors.length > 0 ||
        reportConfig.firdsEsmaInstrumentSelectors.length > 0 ||
        reportConfig.fcaRegulatedEntitiesSelectors.length > 0 ||
        reportConfig.firdsEsmaInstrumentSelectors.length > 0 ||
        reportConfig.leiSelectors.length > 0 ||
        reportConfig.isinSelectors.length > 0 ||
        reportConfig.lseSelectors.length > 0 ||
        reportConfig.symbolSelectors.length > 0;

    switch (module) {
        case OptionalModule.UserDefinedFunctions: return reportConfig.userDefinedFunctions.length > 0;
        case OptionalModule.CacheKeys: return isAnyCacheKeysDefined;
        case OptionalModule.Variables: return reportConfig.variables.length > 0;
        case OptionalModule.Aggregation: return reportConfig.aggregations.length > 0;
        case OptionalModule.Dictionary: return reportConfig.dictionaries.length > 0;
        case OptionalModule.MatchingKeys: return reportConfig.matchingKey !=null;
        case OptionalModule.ExpectedOverReportLogic: return reportConfig.endpointFilters.filter(x=>x.type===endpointFilterType.overReport).length > 0;
        case OptionalModule.ExpectedUnderReportLogic: return reportConfig.endpointFilters.filter(x=>x.type===endpointFilterType.underReport).length > 0;
        case OptionalModule.EndPointFilteringLogic  : return reportConfig.endpointFilters.filter(x=>x.type===endpointFilterType.regular).length > 0;

        default: return false;
    }
}
export {
    getConfigOptionValue,
    getConfigOptionText,
    runAllOptions,
    reportTypeOptions,
    regimeOptions,
    getSubRegimeOptions,
    isDataSourceReferencedInCurrentConfig,
    getModules,
    mapToselectOptions,
    isRemoveOptionalModuleDisabled
};
